import { useContext } from 'react';
import {
  useMutation as useMutationOriginal,
  useQueryClient,
} from 'react-query';

import Axios from 'axios';

import MessageContext from './MessageContext';
import { matchQuery } from './utils';

const defaultErrorMessage = (error) => error.message || error.messages;

export const useMutation = (
  mutationKey,
  mutationFn,
  {
    refresh = true,
    refreshOptions = {
      //'same', 'notSame','all'
      refreshKey: 'same',
      useInvalidate: false,
    },
    multipleErrorMessage = false,
    errorMessage = defaultErrorMessage,
    successMessage,
    mutateMessage,
    onMutate,
    onError,
    onSuccess,
    ...options
  } = {}
) => {
  const { showErrorMessage, showSuccessMessage, showMessage } =
    useContext(MessageContext);

  const queryClient = useQueryClient();

  const { refreshKey = 'same', useInvalidate = false } = refreshOptions;

  return useMutationOriginal(mutationKey, mutationFn, {
    ...options,
    onMutate: (variables) => {
      if (mutateMessage) {
        const message = mutateMessage(variables);
        showMessage(message);
      }
      return onMutate?.(variables);
    },
    onError: (error, variables, context) => {
      if (Axios.isCancel(error)) {
        console.log('[Mutation] cancel error ignore');
        return;
      }
      const message =
        typeof errorMessage === 'function'
          ? errorMessage(error, variables, context)
          : null;
      if (message) {
        showErrorMessage(message, {
          multiple: multipleErrorMessage,
        });
      }
      onError?.(error, variables, context);
      // return Promise.reject(error);
    },

    onSuccess: (data, variables, context) => {
      if (successMessage) {
        const message = successMessage(data, variables, context);

        if (message) {
          showSuccessMessage(message);
        }
      }

      if (refresh) {
        const {
          withoutQueryKey: withoutQueryKeyOption,
          ...refreshOptionsOther
        } = refreshOptions;
        let params = { active: true, ...refreshOptionsOther };

        params.predicate = (query) => {
          return matchQuery(
            {
              refreshKey,
              mutationKey,
              queryKey: refreshOptions.queryKey,
              exact: false,
            },
            query
          );
        };

        if (typeof withoutQueryKeyOption === 'function') {
          const withoutQueryKey = withoutQueryKeyOption(variables);
          params.predicate = (query) => {
            return !matchQuery({ queryKey: withoutQueryKey }, query);
          };

          if (refreshOptionsOther.predicate) {
            throw new Error('Predicate conflict with "withoutQueryKey"');
          }
        }

        if (typeof refreshOptionsOther.queryKey === 'function') {
          refreshOptionsOther.queryKey =
            refreshOptionsOther.queryKey(variables);
        }
        useInvalidate
          ? queryClient.invalidateQueries(params)
          : queryClient.resetQueries(params);
      }

      return onSuccess?.(data, variables, context);
    },
  });
};
